import { useEffect, useState } from "react";
import { Input } from "./../../Components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./../../Components/ui/select";
import { useOverview } from "./../../hooks";
import {
  filterAddressFields,
  filterFields,
} from "./../../Utils/helperFunction";
import { BeniCountryConfig } from "./../../api";
import { Card, CardContent } from "./../../Components/ui/card";
import Loader from "./../../Components/Loader/PageLoader";
import DynamicForm from "./DynamicForm";

function AddBeneficiary({
  HandleSubmit,
  formData,
  errors,
  handleInputChange,
  handlefeildBlur,
  setFormData,
  setErrors,
  setDynamicValidation,
  setDynamicAddressValidation,
}) {
  const {
    data,
    setData,
    selectedMethod,
    setSelectedMethod,
    countryList,
    formattedFields,
    useCountryList,
    setFormattedFields,
    loading,
    setLoading,
  } = useOverview();

  useCountryList();

  const [formattedAddressFields, setFormattedAddressFields] = useState([]);
  const [formLoading, setFormLoading] = useState(false);
  const [methods, setMethods] = useState({});

  // Helper function to get the first valid routing type from validation
  const getFirstValidRoutingType = (formattedFields, fieldName) => {
    const field = formattedFields?.find(f => f.fieldName === fieldName);
    if (field && field.validation) {
      // Check if validation contains pipe-separated options (like "bsb|phone_number|email_address")
      if (field.validation.includes("|")) {
        const options = field.validation.split("|");
        return options[0]; // Return the first valid option
      } else {
        // Handle regex patterns like "^institution_number$" or "^transit_number$"
        const match = field.validation.match(/\^([^$]+)\$/);
        if (match) {
          return match[1]; // Extract the value between ^ and $
        }
        // If no regex pattern, return the validation as is (cleaned)
        return field.validation.replace(/[^a-zA-Z0-9_]/g, "");
      }
    }
    return "";
  };

  // if only method is available then select it
  useEffect(() => {
    if (Object.keys(methods).length === 1) {
      const onlyMethod = Object.keys(methods)[0];
      setSelectedMethod(onlyMethod);
      setFormData({
        ...formData,
        payment_method: onlyMethod,
      });
    }
  }, [methods]);

  // when county is selected auto select other fields
  const countryListData = countryList?.map((account) => ({
    // flag: getFlagByCurrencyCode(account.currency),
    country_name: account.countryName,
    country_code: account.countryCode2,
    account_currency: account.currency,
  }));

  useEffect(() => {
    const fetchMethods = async () => {
      try {
        if (formData.country_code) {
          setLoading(true);
          const result = await BeniCountryConfig({ formData });
          const methodsResponse = result?.data[0]?.methods || [];
          // Sort methods to ensure LOCAL appears first
          const sortedMethods = [...methodsResponse].sort((a, b) => {
            if (a.methodName === "LOCAL") return -1;
            if (b.methodName === "LOCAL") return 1;
            return 0;
          });

          // Dynamically build methods object
          const methods = sortedMethods.reduce((acc, method) => {
            const methodName = method.methodName; // LOCAL or SWIFT
            acc[methodName] = {
              title: methodName === "LOCAL" ? "Borderless Instant" : "SWIFT",
              duration:
                methodName === "LOCAL"
                  ? "(0-1 Business Days)"
                  : "(1-3 Business Days)",
              charges: methodName === "LOCAL" ? "5 USD" : "15 USD",
              recommended: methodName === "LOCAL",
              fields: method.fields.map((field) => ({
                fieldName: field.fieldName,
                dataType: field.dataType,
                required: field.required,
                displayName: field.displayName,
                validation: field.validation,
              })),
            };
            return acc;
          }, {});

          setMethods(methods);
          setLoading(false);
        }
      } catch (error) {
        console.error("Error fetching methods:", error);
        setLoading(false);
      }
    };

    fetchMethods();
  }, [formData.country_code]);

  useEffect(() => {
    const fetchFields = async () => {
      try {
        if (formData.payment_method) {
          setFormLoading(true);
          const result = await BeniCountryConfig({
            formData,
            selectedMethod: formData.payment_method,
          });
          setData(result?.data[0]?.methods[0]?.fields);

          setFormLoading(false);
        }
      } catch (error) {
        console.error("Error fetching fields:", error);
        setFormLoading(false);
      }
    };

    fetchFields();
  }, [formData.payment_method, formData.country_code]);

  useEffect(() => {
    const newFormattedFields = filterFields(data)?.map((field) => ({
      ...field,
      displayName: field.displayName
        ?.split("_")
        .map((word) => word.charAt(0).toUpperCase() + word?.slice(1))
        .join(" "),
    }));
    const newAddressFormattedFields = filterAddressFields(data)?.map(
      (field) => ({
        ...field,
        displayName: field.displayName
          ?.split("_")
          .map((word) => word.charAt(0).toUpperCase() + word?.slice(1))
          .join(" "),
      })
    );

    // Initialize form data and errors based on fields
    const initialErrorData = newFormattedFields.reduce((acc, field) => {
      acc[field.fieldName] = "";
      return acc;
    }, {});

    const hasInitialData = newFormattedFields.some(
      (field) =>
        formData[field.fieldName] !== undefined &&
        formData[field.fieldName] !== ""
    );
    
    if (!hasInitialData) {
      const initialFormData = newFormattedFields.reduce((acc, field) => {
        if (field.fieldName === "account_routing_type1") {
          // Dynamically get the first valid option from validation
          acc[field.fieldName] = getFirstValidRoutingType(newFormattedFields, "account_routing_type1");
        } else if (field.fieldName === "account_routing_type2") {
          // Dynamically get the first valid option from validation
          acc[field.fieldName] = getFirstValidRoutingType(newFormattedFields, "account_routing_type2");
        } else {
          acc[field.fieldName] = "";
        }
        return acc;
      }, {});

      setFormData({ ...formData, ...initialFormData });
    }

    setErrors({ ...errors, ...initialErrorData });
    setFormattedFields(newFormattedFields);
    setFormattedAddressFields(newAddressFormattedFields);
  }, [data]);

  useEffect(() => {
    setDynamicValidation(formattedFields);
    setDynamicAddressValidation(formattedAddressFields);
  }, [formattedFields]);

  const renderCard = (method, methodData) => (
    <Card
      key={method}
      className={`border border-[#D3D4DA] mt-2 ${
        selectedMethod === method ? "bg-[#FFE2C7]" : "bg-white"
      }`}
      onClick={() => {
        setSelectedMethod(method);
        setFormData({
          ...formData,
          payment_method: method,
        });
      }}
    >
      <CardContent className="p-4 relative">
        <div className="flex justify-between items-center">
          <div className="space-y-2">
            <p className="font-geomanistBold text-sm text-blackShade">
              {methodData.title}
            </p>
            <p className="font-normal text-xs text-blackShade">
              {methodData.duration}
            </p>
            <p className="font-normal text-xs text-blackShade">
              Charges: {methodData.charges}
            </p>
          </div>
          <input
            type="radio"
            name="transferMethod"
            checked={selectedMethod === method}
            onChange={() => {
              setSelectedMethod(method);
              setFormData({
                ...formData,
                payment_method: method,
              });
            }}
            className="form-radio"
          />
        </div>
        {methodData.recommended && (
          <div className="absolute -top-3 right-1">
            <span className="rounded-full font-geomanistBold text-[12px] py-[5px] px-[10px] bg-blueish text-white">
              Recommended
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <form className="" onSubmit={HandleSubmit}>
      {/* h-[calc(100dvh-145px)] */}
      <div className=" space-y-4 py-6 mb-4 ">
        <div className="space-y-2">
          <label className="block text-xs text-blackShade font-geomanistBold mb-2">
            Payee Type
          </label>
          <Select
            value={formData.entity_type}
            onValueChange={(value) => handleInputChange("entity_type", value)}
            obBlur={(value) => handlefeildBlur("entity_type", value)}
          >
            <SelectTrigger
              className="w-full font-geomanistBold p-3"
              error={errors?.entity_type}
            >
              <SelectValue placeholder="Select Payee Type">
                <span className="font-geomanistBold">
                  {formData?.entity_type}
                </span>
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="z-[9999]">
              <SelectItem key={"INDIVIDUAL"} value={"INDIVIDUAL"}>
                INDIVIDUAL
              </SelectItem>
              <SelectItem isLastItem={true} key={"BUSINESS"} value={"BUSINESS"}>
                BUSINESS
              </SelectItem>
            </SelectContent>
          </Select>

          {errors.entity_type && (
            <p className="text-xs text-red-500 mt-1">{errors?.entity_type}</p>
          )}
        </div>

        <div className="space-y-2">
          <p className="text-xs font-geomanistBold text-blackShade">
            First Name
          </p>
          <Input
            type="text"
            placeholder=""
            value={formData?.first_name}
            onChange={(e) => handleInputChange("first_name", e.target.value)}
            onBlur={(e) => handlefeildBlur("first_name", e.target.value)}
            className={`text-sm border font-geomanistBold ${
              errors?.first_name ? "border-red-500" : ""
            }`}
          />
          {errors?.first_name && (
            <p className="text-xs text-red-500 mt-1">{errors?.first_name}</p>
          )}
        </div>

        <div className="space-y-2">
          <p className="text-xs font-geomanistBold text-blackShade">
            Last Name
          </p>
          <Input
            type="text"
            placeholder=""
            value={formData?.last_name}
            onChange={(e) => handleInputChange("last_name", e.target.value)}
            onBlur={(e) => handlefeildBlur("last_name", e.target.value)}
            className={`text-sm border font-geomanistBold ${
              errors?.last_name ? "border-red-500" : ""
            }`}
          />
          {errors?.last_name && (
            <p className="text-xs text-red-500 mt-1">{errors?.last_name}</p>
          )}
        </div>

        <div className="space-y-2">
          <label className="block text-xs text-blackShade font-geomanistBold mb-2">
            Payee Country
          </label>
          <Select
            value={formData.country_name}
            onValueChange={(value) => handleInputChange("country_code", value)}
            obBlur={(value) => handlefeildBlur("country_code", value)}
          >
            <SelectTrigger
              className="w-full font-geomanistBold p-3"
              error={errors?.country_code}
            >
              <SelectValue placeholder="Select Country">
                <span className="mr-2">
                  {/* <img
                    src={getFlagByCurrencyCode(formData?.country_name)}
                    alt=""
                  /> */}
                </span>
                <span className="font-geomanistBold">
                  {formData?.country_name}
                </span>
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="z-[9999]">
              {countryListData?.map((currency, index) => (
                <SelectItem
                  isLastItem={index === countryListData?.length - 1}
                  key={currency?.country_name}
                  value={currency?.country_name}
                >
                  <div className="flex items-center">
                    {/* <span className="mr-2">
                      <img src={currency?.flag} alt="" />
                    </span> */}
                    <span className="font-geomanistBold">
                      {currency?.country_name}
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {errors.country_code && (
            <p className="text-xs text-red-500 mt-1">{errors?.country_code}</p>
          )}
        </div>

        <div>
          {loading ? (
            <div className="flex justify-center items-center mt-20 ">
              <Loader className="h-20 w-20" />
            </div>
          ) : (
            Object.keys(methods).length > 0 && (
              <>
                <p className="text-sm font-normal leading-[20px] text-greyish">
                  Choose Transfer Method
                </p>
                {Object.entries(methods).map(([method, data]) =>
                  renderCard(method, data)
                )}
              </>
            )
          )}
        </div>

        <div>
          {formLoading ? (
            <div className="flex justify-center items-center mt-20 ">
              <Loader className="h-20 w-20" />
            </div>
          ) : (
            <DynamicForm
              formData={formData}
              errors={errors}
              handleInputChange={handleInputChange}
              handlefeildBlur={handlefeildBlur}
              formattedFields={formattedFields}
            />
          )}
        </div>
      </div>
    </form>
  );
}

export default AddBeneficiary;